import { Injectable, computed, inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TranslateService } from '@ngx-translate/core';

import type {
    NormalizedBlockEntity,
    NormalizedColumnEntity,
    NormalizedSectionEntity,
    WebLegacyWidgetEntity,
} from '@web/types';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import type { EntityMentionCandidate } from './entity-mention.util';

/**
 * Every block/column/widget on the page, labeled for the "@" mention dropdown — §7's autocomplete.
 *
 * Labeling deliberately mirrors `navigation-drawer/navigation-*-item.component.ts` (the page outline
 * panel), the one other place this app already turns these entities into human-readable list rows,
 * rather than inventing new rules: a block's name is "Header"/"Footer"/"Block" from its own
 * `entitySubType`/`meta.role`, a widget's name is `WebWidgetsService.getWidgetModel(name).label`
 * (the widget TYPE's display name — "Text", "Button" — there is no per-instance title anywhere on
 * the entity), and the `displayId`/`meta.displayId` fields (or a slice of the uuid) give each row a
 * second, more specific anchor. Simplified in one place: a column's label here is always
 * "Column <position in its section>" — the nav panel additionally special-cases the fixed left/
 * center/right columns of a HEADER/FOOTER's specific section, which needs one more hop (the column's
 * section's own parent BLOCK, to read `entitySubType`) that this dropdown does not bother with.
 */
@Injectable({ providedIn: 'root' })
export class EntityMentionService {
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly widgetsService = inject(WebWidgetsService);
    private readonly translate = inject(TranslateService);

    private readonly blocksEntities = toSignal(this.blocksFacade.blocksEntities$, { initialValue: {} });
    private readonly sectionsEntities = toSignal(this.sectionsFacade.sectionsEntities$, { initialValue: {} });
    private readonly columnsEntities = toSignal(this.columnsFacade.columnsEntities$, { initialValue: {} });
    private readonly widgetsEntities = toSignal(this.widgetsFacade.widgetsEntities$, { initialValue: {} });

    /** Every candidate on the current page — filtered against the typed query by the caller. */
    public readonly candidates: Signal<EntityMentionCandidate[]> = computed(() => {
        const blocks = Object.values(this.blocksEntities()).filter((block): block is NormalizedBlockEntity =>
            Boolean(block)
        );
        const sections = this.sectionsEntities();
        const columns = Object.values(this.columnsEntities()).filter((column): column is NormalizedColumnEntity =>
            Boolean(column)
        );
        const widgets = Object.values(this.widgetsEntities()).filter((widget): widget is WebLegacyWidgetEntity =>
            Boolean(widget)
        );

        return [
            ...blocks.map((block) => this.blockCandidate(block)),
            ...columns.map((column) => this.columnCandidate(column, sections)),
            ...widgets.map((widget) => this.widgetCandidate(widget)),
        ];
    });

    private blockCandidate(block: NormalizedBlockEntity): EntityMentionCandidate {
        const isSpecific = block.entitySubType === 'specific';
        const isFooter = block.meta.role === 'footer';
        const nameKey = isSpecific ? 'header' : isFooter ? 'footer' : 'control_block';
        const displayId = block.meta.displayId || block.id.slice(0, 8);

        return { type: 'block', id: block.id, label: `${this.translate.instant(nameKey)} · ${displayId}` };
    }

    private columnCandidate(
        column: NormalizedColumnEntity,
        sections: Record<string, NormalizedSectionEntity | undefined>
    ): EntityMentionCandidate {
        const parentSection = Object.values(sections).find((section) => section?.children.includes(column.id));
        const index = parentSection ? parentSection.children.indexOf(column.id) : -1;
        const name =
            index >= 0
                ? `${this.translate.instant('control_column')} ${index + 1}`
                : this.translate.instant('control_column');
        const displayId = `column-${column.id.slice(0, 8)}`;

        return { type: 'column', id: column.id, label: `${name} · ${displayId}` };
    }

    private widgetCandidate(widget: WebLegacyWidgetEntity): EntityMentionCandidate {
        const model = this.widgetsService.getWidgetModel(widget.name);
        const name = model?.label ? this.translate.instant(model.label) : widget.name;
        const displayId = widget.displayId || `widget-${widget.id.slice(0, 8)}`;

        return { type: 'widget', id: widget.id, label: `${name} · ${displayId}` };
    }
}
